import type { AppProps } from 'next/app';
import { SessionProvider } from 'next-auth/react';
import Head from 'next/head';
import * as React from 'react';

// Styles
import '../styles/variables.css';
import '../styles/global-tailwind.css';
import '../styles/globals.scss';
import '../styles/globals.css';

// Toast styles
import 'react-toastify/dist/ReactToastify.css';

// React query
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { Layout, LayoutDashboard } from 'components/layout';
import { Navbar } from 'components/landing';
import { ToastContainer } from 'react-toastify';
import { ChatMessagesProps, RoleType } from '@/interfaces';
import Script from 'next/script';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import { getUserIp } from '@/lib';
import { useIsSafari } from '@/hooks/useIsSafari';
import AppRoutes from '@/const/routes';
import { ChatMessagesContext } from '../context/chatMessages';
import { ShowChatBoxContext, UnsavedChangesProvider } from '@/context';
import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';
import {
	captureUtmAttribution,
	isAttributionEnabledEnv,
} from '@/lib/utmAttribution';
import { PreventNavigation, UnsavedChangesModal } from 'components/common';

// Extend the Window interface to include 'webkit' and fbq (Meta Pixel)
declare global {
	interface Window {
		webkit?: {
			messageHandlers?: Record<string, unknown>;
		};
		fbq?: (...args: unknown[]) => void;
		_fbq?: unknown;
	}
}

const queryClient = new QueryClient();

const contextClass = {
	success: 'bg-[#E0F4E8] text-[#198443]',
	error: 'bg-[#FED7E1] text-[#FF0043]',
	info: 'bg-[#DDE1FD] text-[#2D41F5]',
	warning: 'bg-[#FFEEDF] text-[#FF8E1F]',
	default: 'bg-indigo-600',
	dark: 'bg-white-600 font-gray-300',
};

export default function App({
	Component,
	pageProps: { session, ...pageProps },
}: AppProps): React.ReactElement {
	const pathname = usePathname();
	const router = useRouter();
	const { isSafari } = useIsSafari();

	const [cookieConsent, setCookieConsent] = React.useState<boolean | null>(
		null
	);

	React.useEffect(() => {
		const storedCookieConsent = getLocalStorage('cookie_consent');
		setCookieConsent(storedCookieConsent ?? false);
		console.log('cookie_consent', cookieConsent);
	}, []);

	const IS_PRODUCTION = process.env.NEXT_PUBLIC_SERVER === 'production';
	const IS_DEVELOP = process.env.NEXT_PUBLIC_SERVER === 'develop';
	const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
	const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;
	const META_PIXEL_ID = '1295480079201392';

	const dashboardRoutes = [AppRoutes.CLIENTAPP, AppRoutes.LAWYERAPP];
	const excludedRoutes = [AppRoutes.CLIENTAPP_ALL_CASES];

	const ANALYTICS_ALLOWED_ROUTES = [
		'/clientapp/auth/signup',
		'/cases/[slug]',
		'/cases/claim-evaluation',
	];

	const isHotjarRoute = ANALYTICS_ALLOWED_ROUTES.includes(router.pathname);

	// Meta Pixel: only load on public landing pages (never on dashboard routes with sensitive forms)
	const isMetaPixelRoute =
		!router.pathname.startsWith('/clientapp') &&
		!router.pathname.startsWith('/lawyerapp') &&
		!router.pathname.startsWith('/retainer');

	// --------------------- Chat IA ---------------------------------//
	const [showChatBox, setShowChatBox] = React.useState<boolean>(false);
	const [chatMessages, setChatMessages] = React.useState<ChatMessagesProps[]>(
		[]
	);
	const [isLoadingChat, setIsLoadingChat] = React.useState<boolean>(false);
	const [isConversationOver, setIsConversationOver] =
		React.useState<boolean>(false);
	const [initialDateChat, setInitialDateChat] = React.useState<string>('');

	React.useEffect(() => {
		getUserIp();
		const isDashboardRouter =
			dashboardRoutes.some((route) => router.pathname.includes(route)) &&
			!excludedRoutes.some((route) => router.pathname.includes(route));

		if (isSafari && isDashboardRouter) {
			document.body.classList.add('safari');
		} else {
			document.body.classList.remove('safari');
		}
	}, [isSafari, router.pathname]);

	// Google Analytics
	React.useEffect(() => {
		if (!isAttributionEnabledEnv() || !GA_MEASUREMENT_ID) return;

		const handleRouteChange = (url: string) => {
			if (typeof (window as any).gtag === 'function') {
				(window as any).gtag('config', GA_MEASUREMENT_ID, {
					page_path: url,
				});
			}
		};

		router.events.on('routeChangeComplete', handleRouteChange);
		return () => {
			router.events.off('routeChangeComplete', handleRouteChange);
		};
	}, [router.events]);

	// Meta Pixel: track PageView on landing page route changes (excluding dashboard/form routes)
	React.useEffect(() => {
		// if (!IS_PRODUCTION) return;
		if (!IS_DEVELOP) return; // TODO: remove this when we have a production environment

		const handleRouteChange = (url: string) => {
			const isSafeRoute =
				!url.startsWith('/clientapp') &&
				!url.startsWith('/lawyerapp') &&
				!url.startsWith('/retainer');
			if (isSafeRoute && typeof window.fbq === 'function') {
				window.fbq('track', 'PageView');
			}
		};

		router.events.on('routeChangeComplete', handleRouteChange);
		return () => {
			router.events.off('routeChangeComplete', handleRouteChange);
		};
	}, [router.events]);

	// Meta Pixel: revoke consent if navigating to protected routes while Pixel is in memory
	React.useEffect(() => {
		if (!isMetaPixelRoute && typeof window.fbq === 'function') {
			window.fbq('consent', 'revoke');
		}
	}, [router.pathname, isMetaPixelRoute]);

	// Hotjar
	React.useEffect(() => {
		if (IS_PRODUCTION && isHotjarRoute) {
			const handleRouteChange = (url: string) => {
				if (typeof (window as any).hj === 'function') {
					(window as any).hj('stateChange', url);
				}
			};

			router.events.on('routeChangeComplete', handleRouteChange);
			return () => {
				router.events.off('routeChangeComplete', handleRouteChange);
			};
		}
	}, [router.events]);

	React.useEffect(() => {
		if (router.pathname !== '/404') {
			sessionStorage.setItem('lastInternalPath', router.asPath);
		}
	}, [router.asPath, router.pathname]);

	React.useEffect(() => {
		const { transaction_id } = router.query;

		// Capture transaction_id from URL and save it in localStorage
		if (transaction_id && typeof transaction_id === 'string') {
			setLocalStorage('transaction_id', transaction_id);
		}

		if (isAttributionEnabledEnv()) {
			captureUtmAttribution(router.query);
		}
	}, [router.query]);

	const getRole = (): RoleType | undefined => {
		if (pathname?.startsWith('/clientapp')) {
			return 'client';
		}
		if (pathname?.startsWith('/lawyerapp')) {
			return 'lawyer';
		}

		return undefined;
	};

	const renderLayout = (): React.ReactElement => {
		if (Component.displayName === 'Custom404') {
			return <Component {...pageProps} />;
		}
		if (
			pathname?.startsWith('/clientapp/auth/') ||
			pathname?.startsWith('/lawyerapp/auth/') ||
			pathname?.startsWith('/cases/claim-evaluation') ||
			pathname?.startsWith('/retainer') ||
			pathname?.startsWith('/clientapp/documents/complaint') ||
			pathname?.startsWith('/clientapp/documents/demand-letter') ||
			pathname?.startsWith('/email-unsubscribe') ||
			pathname?.startsWith('/test-convert-pdf-to-html-endpoint')
		) {
			return <Component {...pageProps} />;
		}
		if (
			(pathname?.startsWith('/clientapp') ||
				pathname?.startsWith('/lawyerapp')) &&
			!pathname?.startsWith('/clientapp/all-cases') // the only one out the dashborad
		) {
			return (
				<LayoutDashboard
					type={getRole()}
					dataCY="dashboardHomePage"
					withSidebar={true}
					withHeader={true}
					withButtonChat={
						!pathname.includes('inbox') &&
						!pathname.includes('customer-support') &&
						getRole() === 'client'
					}
					classNameLayout={`overflow-hidden`}
					pathname={pathname}
				>
					<Component {...pageProps} />
				</LayoutDashboard>
			);
		}
		return (
			<Layout
				withFooter={!pathname?.startsWith('/clientapp/all-cases')}
				dataCY="homePage"
				withSidebar={false}
				customHeader={<Navbar dataCY={'homePage'} />}
				classNameChildren={clsx({
					'!pb-16': pathname?.startsWith('/clientapp/all-cases'),
				})}
				classNameLayout={clsx(
					'bg-gradient-to-b from-[#FFFFFF] to-[#F4F4F9] min-h-full'
				)}
			>
				<Component {...pageProps} />
			</Layout>
		);
	};

	return (
		<>
			<Head>
				<title>Claim Magic</title>
			</Head>

			{isAttributionEnabledEnv() && GTM_ID && cookieConsent && (
				<Script id="gtm-script" strategy="afterInteractive">
					{`
						(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
						new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
						j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
						'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
						})(window,document,'script','dataLayer','${GTM_ID}');
					`}
				</Script>
			)}

			{isAttributionEnabledEnv() && GA_MEASUREMENT_ID && cookieConsent && (
				<>
					<Script
						src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
						strategy="afterInteractive"
					/>
					<Script id="google-analytics" strategy="afterInteractive">
						{`
							window.dataLayer = window.dataLayer || [];
							function gtag(){dataLayer.push(arguments);}
							window.gtag = window.gtag || gtag;
							const path = window.location.pathname;
							gtag('js', new Date());
							gtag('config', '${GA_MEASUREMENT_ID}', {
							page_path: path,
							});
							
							const urlsAllowed = [
								'/clientapp/auth/signin',
								'/clientapp/auth/signup',
								'/clientapp/auth/signup-success',
								'/clientapp/auth/verify',
								'/clientapp/auth/verify-email',
								'/clientapp/auth/verify-email-first-time',
								'/clientapp/all-cases',
							];

							if ((path.includes('/lawyerapp') || path.includes('/clientapp')) && !urlsAllowed.includes(path)) {
								window['ga-disable-${GA_MEASUREMENT_ID}'] = true;
							}
						`}
					</Script>
				</>
			)}
			{IS_PRODUCTION && isHotjarRoute && (
				<>
					<Script id="hotjar-stub" strategy="lazyOnload">
						{`
							window.hj = window.hj || function(){ (window.hj.q = window.hj.q || []).push(arguments) };
							(function(){
								const q = window.hj.q;
								const origPush = q.push.bind(q);
								q.push = function(args) {
								if (args[0] === 'update') {
									return origPush(['stateChange', window.location.pathname]);
								}
								return origPush(args);
								};
							})();
						`}
					</Script>
					<Script id="hotjar-script" strategy="afterInteractive">
						{`
							(function(h,o,t,j,a,r){
								h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
								h._hjSettings={hjid:6454334,hjsv:6};
								a=o.getElementsByTagName('head')[0];
								r=o.createElement('script');r.async=1;
								r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
								a.appendChild(r);
							})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
						`}
					</Script>
				</>
			)}

			{/* Meta Pixel — only on public landing pages, never on dashboard/form routes.
			     autoConfig=false disables Automatic Advanced Matching (AAM) so Facebook
			     cannot auto-scrape form fields from the DOM. */}
			{isMetaPixelRoute && IS_DEVELOP && (
				<>
					<Script id="meta-pixel" strategy="afterInteractive">
						{`
							!function(f,b,e,v,n,t,s)
							{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
							n.callMethod.apply(n,arguments):n.queue.push(arguments)};
							if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
							n.queue=[];t=b.createElement(e);t.async=!0;
							t.src=v;s=b.getElementsByTagName(e)[0];
							s.parentNode.insertBefore(t,s)}(window,document,'script',
							'https://connect.facebook.net/en_US/fbevents.js');
							// Disable Automatic Advanced Matching to prevent DOM scraping of form fields
							fbq('set', 'autoConfig', false, '${META_PIXEL_ID}');
							fbq('init', '${META_PIXEL_ID}');
							fbq('track', 'PageView');
						`}
					</Script>
					<noscript>
						{/* eslint-disable-next-line @next/next/no-img-element */}
						<img
							height="1"
							width="1"
							style={{ display: 'none' }}
							src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`}
							alt=""
						/>
					</noscript>
				</>
			)}

			<SessionProvider
				session={session}
				basePath={
					pathname?.startsWith('/lawyerapp') ? '/api/auth-lawyer' : '/api/auth'
				}
			>
				<QueryClientProvider client={queryClient}>
					<ShowChatBoxContext.Provider value={{ showChatBox, setShowChatBox }}>
						<ChatMessagesContext.Provider
							value={{
								chatMessages,
								setChatMessages,
								isLoading: isLoadingChat,
								setIsLoading: setIsLoadingChat,
								isConversationOver,
								setIsConversationOver,
								date: initialDateChat,
								setDate: setInitialDateChat,
							}}
						>
							<UnsavedChangesProvider>
								<div id="toast-custom-container">
									<ToastContainer
										toastClassName={(context) =>
											contextClass[context?.type || 'default'] +
											' w-fit relative z-30 flex px-4 py-3 min-h-10 rounded-lg justify-between overflow-hidden cursor-pointer'
										}
										bodyClassName={'text-base'}
										position="bottom-right"
									/>
								</div>
								{/* Navigation guards live here to stay mounted during route changes. They handle global navigation concerns (e.g. unsaved changes warnings). */}
								<PreventNavigation />
								<UnsavedChangesModal />
								{renderLayout()}
							</UnsavedChangesProvider>
						</ChatMessagesContext.Provider>
					</ShowChatBoxContext.Provider>
				</QueryClientProvider>
			</SessionProvider>
		</>
	);
}
